import { useState } from 'react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { CompanyAuthorizedSignatories } from '../types';
import { useFirestore } from '../helpers/firebaseContext';

export const useAuthorizedSignatories = (
    customerId: string,
    signatories: CompanyAuthorizedSignatories[],
) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const db = useFirestore();
    const ref = doc(db, 'customers', customerId);

    const saveSignatory = async (
        data: Pick<CompanyAuthorizedSignatories, 'id' | 'role_within_company'>,
        editingOriginal: CompanyAuthorizedSignatories | null,
    ) => {
        setLoading(true);
        setError(null);
        try {
            if (editingOriginal) {
                // only role_within_company is editable
                const updated = signatories.map((s) =>
                    s.id === editingOriginal.id
                        ? { ...s, role_within_company: data.role_within_company }
                        : s
                );
                await updateDoc(ref, { authorized_signatories: updated });
            } else {
                const existing = signatories.find((s) => s.id === data.id);
                if (existing) {
                    setError('Customer already exists as a signatory.');
                    return false;
                }

                const newEntry: CompanyAuthorizedSignatories = {
                    id: data.id,
                    role_within_company: data.role_within_company,
                };
                await updateDoc(ref, { authorized_signatories: arrayUnion(newEntry) });
            }
            return true;
        } catch (e: any) {
            setError(e.message ?? 'Something went wrong.');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const deleteSignatory = async (signatory: CompanyAuthorizedSignatories) => {
        setLoading(true);
        try {
            const updated = signatories.filter((s) => s.id !== signatory.id);
            await updateDoc(ref, { authorized_signatories: updated });
        } catch (e: any) {
            setError(e.message ?? 'Delete failed.');
        } finally {
            setLoading(false);
        }
    };

    return { saveSignatory, deleteSignatory, loading, error, setError };
};